import PubSub from "pubsub-js";
import Chart from 'chart.js/auto';
import merge from 'lodash.merge'
import debounce from 'lodash.debounce';
import moment from 'moment-timezone'

import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-moment';
import roundTo from 'round-to'

Chart.register(annotationPlugin);

class ReadingsChart {

  constructor(chartEl, options = {}) {

    this.options = merge({
      aspectRatio: 2,
      label: 'Temperature',
      borderColor: 'rgb(145,27,27)',
      backgroundColor: 'rgba(239,39,39,1)',
      tickFormat: (value, index, values) => {
        return roundTo(value, 1) + '°' + window.getUserTempPref();
      },
      tooltipFormat: (context) => {
        return context.parsed.y + '°' + window.getUserTempPref();
      },
      valueConversion: value => {
        return window.celsiusToUserTemp(value, 1);
      },
      source: 'temperature',
    }, options);

    this.chartEl = chartEl;
    this.chartInstance = null;
    this.data = null;
    this.lastWindowWidth = window.innerWidth;
    this.currentSensorId = null;
    this.currentDateRange = null;

    PubSub.subscribe('sensor/readings/loaded', (name, data) => {
      this.data = data;
      this.render();
    });

    const debounceResize = debounce(() => {

      const windowDiff = Math.abs(window.innerWidth - this.lastWindowWidth);

      if(
        this.data &&
        windowDiff > 100
      ) {
        this.lastWindowWidth = window.innerWidth;
        this.chartEl.width = '100%';
        this.render();
      }

    }, 200);

    window.addEventListener('resize', debounceResize.bind(this));

  }

  render() {

    let pointRadius = 3;
    let pointHitRadius = 3;

    let aspectRatio = this.options.aspectRatio;
    let timeUnit = null;

    if(window.innerWidth < 800) {
      aspectRatio = 2;
    }

    let animationOption = {};

    animationOption = {
      duration: 0,
    };

    this.currentSensorId = this.data.sensorId;
    this.currentDateRange = this.data.dateRange;

    const readings = JSON.parse(JSON.stringify(this.data.readings[this.options.source]));
    const offlinePeriods = [];

    if (readings.values.length > 0) {

      readings.values.forEach((reading, i) => {

        readings.values[i].x = reading.x;
        readings.values[i].y = this.options.valueConversion(reading.y);

      });

      let minValue = this.options.valueConversion(readings.low);
      let maxValue = this.options.valueConversion(readings.high);

      // settings size of points based on number of points to plot
      // hoverable on desktop
      // only do dots on smaller displays if they're usable

      if(window.innerWidth > 1024) {

        if (readings.values.length > 500) {

          pointRadius = 1;
          pointHitRadius = 1;

        } else if (readings.values.length > 300) {

          pointRadius = 3;
          pointHitRadius = 5;

        } else if (readings.values.length > 100) {

          pointRadius = 4;
          pointHitRadius = 5;

        } else {

          pointRadius = 5;
          pointHitRadius = 10;

        }
      } else {

        if (readings.values.length > 300) {

          pointRadius = 0;
          pointHitRadius = 0;

        } else if (readings.values.length > 100) {

          pointRadius = 4;
          pointHitRadius = 5;

        } else {

          pointRadius = 5;
          pointHitRadius = 10;

        }

      }

      // set time unit based on number of days the range is
      let timeSpan = new Date(readings.values[readings.values.length - 1].x).getTime() - new Date(readings.values[0].x).getTime();
      timeSpan = timeSpan / 1000 / 86400;

      if(timeSpan < 2) {
        timeUnit = 'hour';
      } else if(timeSpan < 7) {
        timeUnit = 'day';
      } else {
        timeUnit = 'week';
      }

      switch(this.data.dateRange) {

        case('three_months'):
        case('30_days'):
        case('last_month'):
        case('this_month'):

          minValue -= 5;
          maxValue += 5;

          break;
        case('last_week'):
        case('this_week'):

          minValue -= 2;
          maxValue += 2;

          break;
        case('24_hours'):
        case('yesterday'):
        case('today'):
          minValue -= 1;
          maxValue += 1;
          break;
      }

      console.log("auto fill", window.currentUser.chart_fill);

      // chart fill - auto scaling options
      if(!window.currentUser.chart_fill || window.currentUser.chart_fill == false || window.currentUser.chart_fill == 0) {

          if(this.options.source === 'humidity') {
            minValue = 0;
            maxValue = 100;
          } else if(this.options.source === 'temperature') {

            if(minValue > 0) {
              minValue = 0;
            } else {
              minValue -= 20;
            }
            maxValue += 20;

          } else if(this.options.source === 'tcTemp') {

            if(minValue > 0) {
              minValue = 0;
            } else {
              minValue -= 30;
            }
            maxValue += 30;

          }
      }

      console.log({minValue, maxValue})

      // if existing chart context, update
      if(this.chartInstance) {

        this.chartInstance.destroy();

      }

      const boxAnnotations = {};

      if(offlinePeriods.length > 0) {
        offlinePeriods.forEach(period => {

          if(period.end !== -1) {

            boxAnnotations.box1 = {
              type: 'box',
              xMin: readings.values[period.start].x,
              xMax: readings.values[period.end].x,
              backgroundColor: "rgba(200, 200, 200, .5)",
              borderColor: "rgba(200, 200, 200, .7)",
            };

          }

        });
      }

      const skipped = (ctx, value) => ctx.p0.skip || ctx.p1.skip ? value : undefined;

      this.chartInstance = new Chart(this.chartEl.getContext('2d'), {
        type: 'line',
        data: {
          datasets: [{
            label: this.options.label,
            backgroundColor: this.options.backgroundColor,
            borderColor: this.options.borderColor,
            data: readings.values,
            fill: true,
            pointRadius,
            pointHitRadius,
            segment: {
              borderColor: ctx => skipped(ctx, 'rgb(0,0,0,0.3)'),
              borderDash: ctx => skipped(ctx, [6, 6]),
            },
            spanGaps: true,
            tension: 0.45,
            cubicInterpolationMode: 'default',
          }]
        },
        options: {
          animation: animationOption,
          aspectRatio,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: this.options.tooltipFormat
              }
            },
            annotation: {
              annotations: boxAnnotations
            }
          },
          scales: {
            x: {
              type: 'time',
              time: {
                tooltipFormat: 'MMM D h:mm a',
                unit: timeUnit,
                parser: momentItem => {

                  let m = moment(momentItem);

                  if(window.currentUser.use_local_timezone && window.currentUser.use_local_timezone == 0) {
                    m.tz( window.currentUser.timezone);
                  }

                  return m.format('MMM D, Y h:mm a')

                },
              },
            },
            y:
              {
                ticks: {
                  callback: this.options.tickFormat,
                },
                suggestedMin: minValue,
                suggestedMax: maxValue,
              }

          },
        },
      });

    }

  }

}

export default ReadingsChart;
